import React, { useEffect, useState } from "react";
import { ref, get, set } from "firebase/database";
import { db } from "../../lib/firebase";
import "./MetaConfig.css";

import { CONFIG } from "../../config";
/** ---- Config ---- */
const UPLOAD_URL  = CONFIG.UPLOAD_URL
const IMAGES_BASE = CONFIG.IMAGES_BASE

export default function MetaConfig() {
  const [meta, setMeta] = useState({
    siteTitle: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    icon: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Load from Firebase
  useEffect(() => {
    (async () => {
      try {
        const snap = await get(ref(db, "metaconfig"));
        if (snap.exists()) setMeta((prev) => ({ ...prev, ...snap.val() }));
      } catch (e) {
        setError("Failed to load meta configuration");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (field, value) =>
    setMeta((prev) => ({ ...prev, [field]: value }));

  // Upload icon
  const handleIconUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      setError("");
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(UPLOAD_URL, { method: "POST", body: form });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      if (!data.filename) throw new Error("No filename returned");
      setMeta((prev) => ({ ...prev, icon: data.filename }));
      setSuccess("Icon uploaded successfully ✔");
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Save config
  const saveMeta = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await set(ref(db, "metaconfig"), meta);
      setSuccess("Meta configuration saved successfully ✔");
    } catch (e) {
      setError("Failed to save meta configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="meta-loading">Loading…</div>;

  return (
    <section className="meta-wrap">
      <h2 className="meta-title">Meta Configuration (SEO)</h2>

      {success && <p className="success">{success}</p>}
      {error && <p className="error">{error}</p>}

      <div className="meta-form">
        <div className="row">
          <label className="label">Website Title</label>
          <input
            type="text"
            value={meta.siteTitle}
            onChange={(e) => handleChange("siteTitle", e.target.value)}
            placeholder="e.g., Regency Interiors"
          />
        </div>

        <div className="row">
          <label className="label">Page Icon (Favicon)</label>
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={(e) => handleIconUpload(e.target.files?.[0])}
          />
          {uploading && <p className="note">Uploading...</p>}
          {meta.icon && (
            <div className="icon-preview">
              <img
                src={`${IMAGES_BASE}/${encodeURIComponent(meta.icon)}`}
                alt="favicon"
                className="favicon-preview"
              />
              <p className="icon-name">{meta.icon}</p>
            </div>
          )}
        </div>

        <div className="row">
          <label className="label">Meta Title</label>
          <input
            type="text"
            value={meta.metaTitle}
            onChange={(e) => handleChange("metaTitle", e.target.value)}
            placeholder="e.g., Regency Interiors | Luxury Home Design"
          />
        </div>

        <div className="row">
          <label className="label">Meta Description</label>
          <textarea
            rows="3"
            value={meta.metaDescription}
            onChange={(e) => handleChange("metaDescription", e.target.value)}
            placeholder="Short description that appears in Google search results."
          />
        </div>

        <div className="row">
          <label className="label">Meta Keywords</label>
          <input
            type="text"
            value={meta.metaKeywords}
            onChange={(e) => handleChange("metaKeywords", e.target.value)}
            placeholder="interior design, modular kitchen, home decor"
          />
        </div>

        <div className="actions">
          <button onClick={saveMeta} disabled={saving}>
            {saving ? "Saving…" : "Save to Firebase"}
          </button>
        </div>
      </div>

      <h3 className="preview-title">Live Preview</h3>
      <div className="meta-preview">
        {meta.icon && (
          <img
            src={`${IMAGES_BASE}/${encodeURIComponent(meta.icon)}`}
            alt="favicon"
            className="favicon-large"
          />
        )}
        <h4>{meta.metaTitle || meta.siteTitle || "Your site title here"}</h4>
        <p className="desc">
          {meta.metaDescription ||
            "Your page meta description will be shown here."}
        </p>
        {meta.metaKeywords && (
          <p className="keywords">
            <strong>Keywords:</strong> {meta.metaKeywords}
          </p>
        )}
      </div>
    </section>
  );
}
